import createCache from '@emotion/cache';
import { CacheProvider, css, Global } from '@emotion/react';
import { ClientLoggerProvider } from '@landsbankinn/client-logger';
import { LocaleContext, type LocaleType } from '@landsbankinn/l16n';
import type { StyledTheme } from '@landsbankinn/ui-kit';
import 'focus-visible';
import React, { useMemo, useState, type ReactNode } from 'react';
import { Route, Routes } from 'react-router';
import {
  LoggerServiceFactory,
  type ILoggerService,
} from 'src/services/LoggerService';
import { Consent } from './components/Consent';
import { Error } from './components/Error';
import { MarketingAreaContextProvider } from './components/Layout/Desktop/MarketingContent/MarketingAreaContext';
import { AppContextProvider } from './components/Login/Form/App/AppContext';
import { Login } from './components/Login/Login';
import { Logout } from './components/Logout';
import { SelectAccount } from './components/SelectAccount/SelectAccount';
import { UIKitProvider } from './components/UIKitProvider';
import {
  DEFAULT_CULTURE,
  getCurrentCulture,
  setCurrentCulture,
} from './culture';
import { idpRes } from './Idp.res';
import {
  ApplicationClientContextProvider,
  ApplicationServerContextProvider,
  useApplicationServerContext,
} from './state';

function Localization(props: { children?: ReactNode }) {
  const [locale, setLocale] = useState<LocaleType>(DEFAULT_CULTURE);
  React.useEffect(() => {
    const currentCulture = getCurrentCulture();
    setLocale(currentCulture);
    document.title = idpRes.title[currentCulture];
  }, [locale]);

  function handleLocaleChange(l: LocaleType) {
    setLocale(l);
    setCurrentCulture(l);
  }

  return (
    <LocaleContext.Provider value={{ locale, setLocale: handleLocaleChange }}>
      {props.children}
    </LocaleContext.Provider>
  );
}

const globalStyles = (theme: StyledTheme) => {
  return css`
    body {
      background-color: ${theme.colors.blue.baseLight};
    }
  `;
};

export function Emotions(props: { children?: ReactNode }) {
  const [state] = useApplicationServerContext();
  const cache = useMemo(
    () => createCache({ key: 'idp', nonce: state.nonce }),
    [state.nonce],
  );

  return <CacheProvider value={cache}>{props.children}</CacheProvider>;
}

/**
 * This view is used by the production App but can also be used in stories during UI testing
 * @returns An App view that needs to be wrapped in context
 */
export function IdpView({ children }: { children?: ReactNode }) {
  return (
    <ApplicationClientContextProvider>
      <MarketingAreaContextProvider>
        <AppContextProvider>
          <Emotions>
            <UIKitProvider>
              <Global styles={globalStyles} />
              <Routes>
                <Route path="/selectaccount" element={<SelectAccount />} />
                <Route path="/login/*" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/error" element={<Error />} />
                <Route path="/consent" element={<Consent />} />
              </Routes>
              {children}
            </UIKitProvider>
          </Emotions>
        </AppContextProvider>
      </MarketingAreaContextProvider>
    </ApplicationClientContextProvider>
  );
}

function Idp({ children }: { children?: ReactNode }) {
  const logger: ILoggerService = useMemo(() => {
    return LoggerServiceFactory.create('App');
  }, []);

  return (
    <ClientLoggerProvider logger={logger}>
      <ApplicationServerContextProvider>
        <Localization>
          <IdpView children={children} />
        </Localization>
      </ApplicationServerContextProvider>
    </ClientLoggerProvider>
  );
}
export default Idp;
